import { Icon, InfoTag } from "@components/library";
import { COLORS, FONTS, STYLES } from "@constants";
import { setShowErrorBoundary } from "@redux/actions/defaultActions";
import { RootState } from "@redux/store";
import { discover_path, innovators_path } from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { track } from "@utils/appUtils";
import { ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import styled from "styled-components";

interface Props {
  to: string;
  children: ReactNode;
  asAnchor?: boolean;
  withNewTag?: boolean;
  "data-testid"?: string;
  activeStylesMatchType?: "startsWith" | "exact";
  dropdownItems?: {
    label: ReactNode;
    to: string;
  }[];
}

function NavbarLink({
  to,
  children,
  asAnchor = false,
  withNewTag = false,
  "data-testid": dataTestId,
  activeStylesMatchType = "exact",
  dropdownItems,
}: Props) {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const showErrorBoundary = useSelector(
    (state: RootState) => state.defaultReducer.showErrorBoundary
  );

  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const handleClick = () => {
    if (to.startsWith(discover_path())) {
      track(SegmentEventName.CLICK, {
        ui_component: "Discover Nav Click",
      });
    }
    if (to.startsWith(marketplacePath())) {
      track(SegmentEventName.CLICK, {
        ui_component: "Marketplace Nav Click",
        react_component: "NavbarLink",
      });
    }
    if (to.startsWith(innovators_path())) {
      track(SegmentEventName.CLICK, {
        ui_component: "Innovators Nav Click",
        react_component: "NavbarLink",
      });
    }
    if (to.startsWith("https://blog.halo.science/")) {
      track(SegmentEventName.CLICK, {
        ui_component: "Blog Nav Click",
        react_component: "NavbarLink",
      });
    }

    if (showErrorBoundary) {
      dispatch(setShowErrorBoundary(false));
      window.location.href = to;
    }
  };

  let isActive = false;
  if (activeStylesMatchType === "startsWith") {
    isActive = String(pathname).startsWith(to) && !showErrorBoundary;
  } else if (activeStylesMatchType === "exact") {
    isActive = String(pathname) === to && !showErrorBoundary;
  }

  if (!isActive && dropdownItems && !showErrorBoundary) {
    isActive = dropdownItems.some((item) => {
      if (activeStylesMatchType === "startsWith") {
        return String(pathname).startsWith(item.to);
      }
      return String(pathname) === item.to;
    });
  }

  return (
    <Container
      isActive={isActive}
      onMouseEnter={dropdownItems ? () => setIsDropdownVisible(true) : undefined}
      onMouseLeave={dropdownItems ? () => setIsDropdownVisible(false) : undefined}
    >
      <Link
        to={to}
        as={asAnchor ? "a" : NavLink}
        href={asAnchor && to}
        onClick={handleClick}
        $isActive={isActive}
        data-testid={dataTestId}
      >
        {children}
        {withNewTag && (
          <InfoTag accent marginLeft="8px">
            BETA
          </InfoTag>
        )}
        {dropdownItems && <Icon name="Chevron Down" size="xs" margin="0 0 0 8px" />}
      </Link>
      {dropdownItems && isDropdownVisible && (
        <DropdownContainer>
          {dropdownItems.map((item) => (
            <DropdownItem key={item.label} to={item.to}>
              {item.label}
            </DropdownItem>
          ))}
        </DropdownContainer>
      )}
    </Container>
  );
}

export default NavbarLink;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-width: fit-content;
  padding: 6px 16px 0;
  border-bottom: 3px solid ${({ isActive }) => (isActive ? COLORS.HALO_BLUE : "transparent")};
  position: relative;
`;
const Link = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  ${FONTS.MEDIUM_2};
  color: ${COLORS.BLACK};
  &:hover,
  &:focus {
    color: ${COLORS.HALO_BLUE};
    text-decoration: none;
  }
  ${({ $isActive }) =>
    $isActive &&
    `
      color: ${COLORS.HALO_BLUE};
      text-decoration: none;
    `}
`;

const DropdownContainer = styled.div`
  display: flex;
  padding: 16px 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  position: absolute;
  left: 0;
  top: 100%;
  border-radius: 8px;
  background: ${COLORS.WHITE};
  box-shadow: ${STYLES.SHADOW_B};
  z-index: 1000;
  min-width: 243px;
`;

const DropdownItem = styled(NavLink)`
  display: flex;
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
  gap: 10px;
  height: 44px;
  padding: 10px 14px;
  border-radius: 4px;
  color: ${COLORS.BLACK};
  text-decoration: none;
  ${FONTS.MEDIUM_2};
  white-space: nowrap;

  &:hover,
  &:focus {
    color: ${COLORS.HALO_BLUE};
    text-decoration: none;
  }
`;

export const DropdownLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  justify-content: space-between;
`;
